/**
 * The lookup itself — no React, so it is testable without a tree and callable from the
 * places that are not components (D7).
 *
 * Three rules, and the third is the one worth knowing:
 *
 * 1. **`en` is the floor.** Every lookup falls through the fetched bundle to the generated
 * `EN`, so a locale that is missing a key renders English rather than a blank. The API
 * merges the same way (`quest/api/i18n.py`), which means a half-translated locale
 * degrades key by key on both sides of the wire.
 * 2. **Parameters are `{named}`**, matching the Python side's `str.format` so one string can
 * be read by either. An unfilled placeholder is left standing rather than blanked: seeing
 * `Day {day}` on screen says exactly what is wrong.
 * 3. **A missing key renders the key.** The API's own `translate()` returns `None` instead,
 * and for good reason — it is joining names a player would half-believe. Here the key set
 * is generated from the bundle and checked at build time, so a miss is a bug in this
 * client, and `ui.turn.submit` in a button is the loudest way to say so.
 */
import { EN, type UiKey, type UiPluralKey } from '@/i18n/en.generated';

export type TranslateParams = Record<string, string | number>;

/** What the components call. Curried out of `makeTranslator` so it is one stable value. */
export interface Translate {
  (key: UiKey, params?: TranslateParams): string;
  /** Counted phrases. `count` picks the CLDR category; the family is the key. */
  plural: (key: UiPluralKey, count: number, params?: TranslateParams) => string;
  /** The locale this translator resolves in — `Intl` callers need it too. */
  locale: string;
}

/** Kept identical to `quest.api.i18n.PLURAL_MARKER`, which is where the rule is argued. */
const PLURAL_MARKER = '.plural.';

/**
 * `ua` is the tag the API and the database use; **`uk` is the tag `Intl` knows.** Ukrainian
 * is `uk` in BCP-47 and always has been — `ua` is the country code — so every `Intl` call
 * has to be handed the mapped tag or it throws `RangeError` on a locale we support. Mapping
 * here rather than renaming the locale keeps one string in the URL, the profile, the story
 * row and the bundle filename.
 */
const INTL_TAGS: Record<string, string> = { ua: 'uk' };

export function intlLocale(locale: string): string {
  return INTL_TAGS[locale] ?? locale;
}

export function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replaceAll(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * A translator over one locale and one (possibly empty) fetched bundle.
 *
 * `Intl.PluralRules` is constructed once per translator rather than per call: it is the
 * expensive part of a plural lookup and the ticker renders a lot of them. An unsupported
 * tag falls back to English's rules, which is the same floor the strings use.
 */
export function makeTranslator(locale: string, strings: Readonly<Record<string, string>>) {
  const plurals = pluralRules(locale);

  const lookup = (key: string): string | undefined => strings[key] ?? EN[key];

  const translate = ((key: UiKey, params?: TranslateParams) =>
    interpolate(lookup(key) ?? key, params)) as Translate;

  translate.plural = (key: UiPluralKey, count: number, params?: TranslateParams) => {
    const category = plurals.select(count);
    const template =
      lookup(`${key}${PLURAL_MARKER}${category}`) ?? lookup(`${key}${PLURAL_MARKER}other`) ?? key;
    return interpolate(template, { count, ...params });
  };

  translate.locale = locale;

  return translate;
}

function pluralRules(locale: string): Intl.PluralRules {
  try {
    return new Intl.PluralRules(intlLocale(locale));
  } catch {
    return new Intl.PluralRules('en');
  }
}
